import { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import BEM from '../bem';
import { User, Group, Conversation, Role } from '../../types';
import { mobxInjectSelect } from '../utils';
import TCClient from '../../client';
import { ReactComponent as ArrowDownSvg } from '../images/arrow-down.svg';
import { ReactComponent as ArrowUpSvg } from '../images/arrow-up.svg';
import { ReactComponent as CancelSvg } from '../images/cancel.svg';
import { ReactComponent as AddLoadingSvg } from '../images/add-loading.svg';
import { fetchTeamMembers, ForwardedEntity } from '../utils/autoForward';
import { DelegationMemberInfo, EntityAvatar, Scrollbars, TeamName, UserName } from './index';

const classes = BEM.with('AutoForwardRecipientsInfoPane');

type RecipientEntity = {
  type: 'user' | 'team' | 'role';
  token: string;
  displayName?: string;
  user?: User;
  role?: Role;
  teamMembers?: User[];
  teamToken?: string;
  teamMemberIsRole?: boolean[];
};

type AutoForwardRecipientsInfoPaneOwnProps = {
  autoForwardRecipients: (ForwardedEntity | undefined)[];
  user: User;
  groupId?: string;
  groupType?: string;
  organizationId: string;
};

type AutoForwardRecipientsInfoPaneMobxProps = {
  closeInfoPane: () => void;
  currentConversation: Conversation;
};

type AutoForwardRecipientsInfoPaneProps = AutoForwardRecipientsInfoPaneOwnProps &
  AutoForwardRecipientsInfoPaneMobxProps;

function AutoForwardRecipientsInfoPaneComponent({
  autoForwardRecipients,
  user,
  groupId,
  groupType,
  organizationId,
  closeInfoPane,
  currentConversation,
}: AutoForwardRecipientsInfoPaneProps) {
  const [recipientEntities, setRecipientEntities] = useState<RecipientEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const [loadingTeams, setLoadingTeams] = useState<Set<string>>(new Set());

  const group = currentConversation?.counterParty as Group;
  const memberIds = useMemo(
    () => group?.members?.map((m) => m.botUser?.id || m.id) || [],
    [group?.members]
  );

  const refetchTeamMembers = useCallback(
    async (teamToken: string): Promise<boolean> => {
      setLoadingTeams((prev) => new Set(prev).add(teamToken));

      try {
        const result = await fetchTeamMembers(teamToken, organizationId);
        if (result) {
          setRecipientEntities((prev) =>
            prev.map((entity) =>
              entity.token === teamToken
                ? {
                    ...entity,
                    teamMembers: result.members,
                    teamMemberIsRole: result.memberIsRole,
                  }
                : entity
            )
          );
          return true;
        }
        return false;
      } catch (error) {
        console.error(`Failed to refetch team ${teamToken}:`, error);
        return false;
      } finally {
        setLoadingTeams((prev) => {
          const next = new Set(prev);
          next.delete(teamToken);
          return next;
        });
      }
    },
    [organizationId]
  );

  const toggleTeam = async (teamId: string) => {
    const isExpanding = !expandedTeams.has(teamId);

    if (isExpanding) {
      const success = await refetchTeamMembers(teamId);
      if (success) {
        setExpandedTeams((prev) => new Set(prev).add(teamId));
      }
    } else {
      setExpandedTeams((prev) => {
        const next = new Set(prev);
        next.delete(teamId);
        return next;
      });
    }
  };

  const processRecipientEntity = useCallback(
    async (entity: ForwardedEntity | undefined): Promise<RecipientEntity | null> => {
      if (!entity) return null;

      if (entity.entityType === 'account') {
        try {
          const forwardedUser = await TCClient.users.find(entity.token, {
            bypassCache: true,
            organizationId,
          });
          if (forwardedUser) {
            return {
              type: 'user',
              token: entity.token,
              user: forwardedUser,
            };
          }
        } catch (error) {
          console.error(`Failed to fetch user ${entity.token}:`, error);
        }
      } else if (entity.entityType === 'team') {
        const result = await fetchTeamMembers(entity.token, organizationId);
        if (result) {
          return {
            type: 'team',
            token: entity.token,
            displayName: entity.displayName,
            teamToken: entity.token,
            teamMembers: result.members,
            teamMemberIsRole: result.memberIsRole,
          };
        }
      } else if (entity.entityType === 'role') {
        try {
          const role = await TCClient.roles.find(entity.token, organizationId);
          return {
            type: 'role',
            token: entity.token,
            displayName: entity.displayName,
            role,
          };
        } catch (error) {
          console.error(`Failed to fetch role ${entity.token}:`, error);
        }
      }

      return null;
    },
    [organizationId]
  );

  const fetchRecipients = useCallback(async () => {
    setIsLoading(true);
    try {
      const entities: RecipientEntity[] = [];
      if (autoForwardRecipients && Array.isArray(autoForwardRecipients)) {
        for (const entity of autoForwardRecipients) {
          const processedEntity = await processRecipientEntity(entity);
          if (processedEntity) {
            entities.push(processedEntity);
          }
        }
        setRecipientEntities(entities);
      }
    } catch (error) {
      console.error('Failed to fetch recipients:', error);
    } finally {
      setIsLoading(false);
    }
  }, [autoForwardRecipients, processRecipientEntity]);

  useEffect(() => {
    fetchRecipients();
  }, [fetchRecipients]);

  useEffect(() => {
    setExpandedTeams(new Set());
  }, [user?.id]);

  const TeamRow = ({ team, isExpanded }: { team: RecipientEntity; isExpanded: boolean }) => {
    const teamEntity = {
      $entityType: 'team',
      displayName: team.displayName || '',
      token: team.token,
    };
    const isLoading = loadingTeams.has(team.token);

    return (
      <div className={classes('team-row')}>
        <div className={classes('team-header')}>
          <div className={classes('team-info')} onClick={() => toggleTeam(team.token)}>
            <EntityAvatar entity={teamEntity} size={30} />
            <div className={classes('team-name-container')}>
              <TeamName
                className={classes('team-name')}
                team={teamEntity}
                ariaLabel={teamEntity.displayName}
                data-test-id="auto-forward-team-name"
              />
              <div
                className={classes('team-member-count')}
                data-test-id="auto-forward-team-member-count"
              >
                {team.teamMembers?.length} members
              </div>
            </div>
            {isLoading ? (
              <AddLoadingSvg
                className={classnames(classes('team-icon'), classes('team-icon--spinning'))}
                data-test-id="team-icon-spinning"
              />
            ) : isExpanded ? (
              <ArrowUpSvg
                className={classes('team-arrow-icon')}
                data-test-id="team-arrow-up-icon"
              />
            ) : (
              <ArrowDownSvg
                className={classes('team-arrow-icon')}
                data-test-id="team-arrow-down-icon"
              />
            )}
          </div>
        </div>
        {isExpanded &&
          team.teamMembers?.map((member, index) => {
            const isRole = team.teamMemberIsRole?.[index] ?? false;
            return (
              <div key={member.id} className={classes('team-member-item')}>
                <DelegationMemberInfo
                  member={member}
                  memberIds={memberIds}
                  groupId={groupId}
                  groupType={groupType}
                  isInInfoPane
                  isTeamMember
                  smallOwnerName={isRole}
                  groupMembers={group?.members}
                />
              </div>
            );
          })}
      </div>
    );
  };

  return (
    <div className={classes()}>
      <div className={classes('header')}>
        <h3 className={classes('title')}>Forwarding Recipients</h3>
        <CancelSvg className={classes('cancel-icon')} onClick={closeInfoPane} />
      </div>
      <div className={classes('scrollbars-wrapper')}>
        <Scrollbars>
          <div className={classes('content')}>
            {isLoading ? (
              <div className={classes('loading')}>Loading recipients...</div>
            ) : recipientEntities.length === 0 ? (
              <div className={classes('empty')}>No recipients found</div>
            ) : (
              <div className={classes('recipients-list')}>
                {user && (
                  <div className={classes('auto-forward-label')} data-test-id="auto-forward-label">
                    <span className={classes('auto-forward-user-name')}>
                      <UserName user={user} />
                    </span>
                    {' is auto-forwarding to:'}
                  </div>
                )}
                {recipientEntities.map((entity) => {
                  if (entity.type === 'user' && entity.user) {
                    return (
                      <div key={entity.token}>
                        <DelegationMemberInfo
                          member={entity.user}
                          memberIds={memberIds}
                          groupId={groupId}
                          groupType={groupType}
                          isInInfoPane
                          groupMembers={group?.members}
                        />
                      </div>
                    );
                  } else if (entity.type === 'team') {
                    return (
                      <TeamRow
                        key={entity.token}
                        team={entity}
                        isExpanded={expandedTeams.has(entity.token)}
                      />
                    );
                  } else if (entity.type === 'role' && entity.role?.botUser) {
                    return (
                      <div key={entity.token}>
                        <DelegationMemberInfo
                          member={entity.role.botUser}
                          memberIds={memberIds}
                          groupId={groupId}
                          groupType={groupType}
                          isInInfoPane
                          smallOwnerName
                          groupMembers={group?.members}
                        />
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            )}
          </div>
        </Scrollbars>
      </div>
    </div>
  );
}

export default mobxInjectSelect<
  AutoForwardRecipientsInfoPaneOwnProps,
  AutoForwardRecipientsInfoPaneMobxProps
>({
  conversationStore: ['currentConversation'],
  messengerStore: ['closeInfoPane'],
})(observer(AutoForwardRecipientsInfoPaneComponent));
